<script setup lang="ts">
import organizationApi from '@/services/organization.service'
import { EDeviceActions } from '@/store/deviceStore/deviceStoreTypes'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { RentalModel } from '@/types/models/RentalModel'
import { ERentalsPerm } from '@/types/permissions/RentalsPermissionsEnum'
import {
    FullScreenLoader,
    FullTableLayout,
    InformationBaseCard,
    PermissionWrapper,
    StackLayout,
    TypographyItem
} from '@rfc/rfc-components-library'
import { useTableModel } from '@rfc/rfc-components-library/hooks'
import { EStoreModules } from '@rfc/rfc-components-library/store'
import {
    EProviders,
    ESortBy,
    ESortByDate,
    type Columns,
    type DateProviderType,
    type TableActionColumnProps
} from '@rfc/rfc-components-library/types'
import moment from 'moment'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

const store = useStore()

const { DATETIME_FORMAT } = inject<DateProviderType>(EProviders.DATE, {})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

type Props = {
    data?: DeviceModel
}
const props = defineProps<Props>()

const columns = computed<Columns[]>(() => [
    { value: 'renter', title: t('device.rentalsHistory.table.renter') },
    {
        value: 'start_at',
        title: t('device.rentalsHistory.table.rental_start_at')
    },
    {
        value: 'end_at',
        title: t('device.rentalsHistory.table.rental_end_at')
    }
])
const routeOrganizationId = computed(() =>
    route?.params?.organization_id ? parseInt(route.params.organization_id?.toString()) : undefined
)
const organizationId = computed(() => store?.state.authentication.me?.organization_id)

const rentalHistoryModel = useTableModel<RentalModel>({
    store,
    stateKey: EStoreModules.DEVICE,
    stateAction: EDeviceActions.GET_DEVICE_RENTALS_HISTORY,
    router,
    options: {
        organizationId: routeOrganizationId.value ?? organizationId.value!,
        deviceId: props.data?.device_id ?? !props.data?.id
    },
    dataSort: ESortBy.DATE_RECENT,
    avoidOnMountedGet: true
})

watch(
    () => props.data,
    newValue =>
        newValue?.device_id || newValue?.id
            ? rentalHistoryModel.handleOptionsChange({
                  deviceId: newValue?.device_id ?? newValue?.id
              })
            : undefined,
    { immediate: true, deep: true }
)

const deleteRental = (rentalId: number) => {
    if (
        (!props.data?.device_id && props.data?.id) ||
        (!routeOrganizationId.value && !organizationId.value)
    )
        return
    organizationApi
        .endDeviceRental(
            routeOrganizationId.value ?? organizationId.value!,
            props.data?.device_id ?? props.data?.id!,
            rentalId
        )
        .then(() => rentalHistoryModel.getData())
}

const table = ref()

const actionProps: TableActionColumnProps = {
    deletePermission: {
        permission: ERentalsPerm.RENTALS_DELETE
    }
}
</script>

<template>
    <PermissionWrapper :permission="ERentalsPerm.RENTALS_VIEW">
        <InformationBaseCard :label="$t('device.rentalsHistory.title')" class="w-full fit-height">
            <StackLayout direction="column" class="w-full" :gap="8">
                <FullScreenLoader :loading="rentalHistoryModel.loading.value" />
                <FullTableLayout
                    ref="table"
                    :columns="columns"
                    :data="rentalHistoryModel.data.value"
                    :loading="rentalHistoryModel.loading.value"
                    :sortBy="rentalHistoryModel.sort.value"
                    :orderOptions="ESortByDate"
                    @change:pageNumber="rentalHistoryModel.handleChangePageNumber"
                    @change:pageSize="rentalHistoryModel.handleChangePageSize"
                    @changeSortBy="rentalHistoryModel.handleChangeSortBy"
                    @removeTag="rentalHistoryModel.handleRemoveTag"
                    @search="rentalHistoryModel.handleSearch"
                    :actionProps="actionProps"
                    @delete="deleteRental($event?.id)"
                    class="device-history">
                    <template #start_at="item">
                        <TypographyItem
                            :label="
                                item.start_at
                                    ? moment(item.start_at).format(DATETIME_FORMAT).toString()
                                    : $t('common.undefined')
                            " />
                    </template>
                    <template #end_at="item">
                        <TypographyItem
                            :label="
                                item.end_at
                                    ? moment(item.end_at).format(DATETIME_FORMAT).toString()
                                    : $t('common.undefined')
                            " />
                    </template>
                    <template #renter="item">
                        <TypographyItem :label="item.renter.name" />
                    </template>
                </FullTableLayout>
            </StackLayout>
        </InformationBaseCard>
    </PermissionWrapper>
</template>

<style scoped lang="scss">
.centered-pagination {
    padding: 8px;
}

.device-history {
    & :deep(.stack) {
        flex-direction: row;
    }
}
</style>
